/**
 * The cohort predicate DSL — the one piece of the ViewSpec contract implemented twice.
 *
 * `evalCohort` mirrors `backend/src/vision_intelligence/agent/specs.py` + the Python evaluator
 * EXACTLY: a SKU is in the cohort iff every condition in `all` holds (AND), and — when `ids` is
 * present — its id is also in `ids`. Both languages are asserted against the same golden fixture
 * (`backend/tests/golden/spec_dsl.json` ↔ `scripts/spec-dsl.check.mts`), so a behavioural drift
 * here fails CI before a pinned view can contradict the agent's own chart.
 *
 * `resolveGroupQuery` and `binDistribution` are NOT golden-fixture-covered (no server
 * counterpart renders them) — they are the live-materialisation half of `bar`/`distribution`
 * views, resolved entirely on the client against the current snapshot.
 */

import type {
  CohortDef,
  FlagCondition,
  GroupQuery,
  NumericCondition,
  StringCondition,
} from "@/lib/api";
import type { Sku } from "@/lib/data";

type Condition = NumericCondition | StringCondition | FlagCondition;
/** `GroupQuery` inlines these literal unions rather than naming them, so name them here. */
type GroupByField = GroupQuery["group_by"];
type Metric = NonNullable<GroupQuery["metric"]>;

/**
 * Runtime discrimination that does not depend on `kind` being present: the generated types mark
 * `kind` optional (Pydantic's discriminator default), so an agent-authored spec that omits it is
 * still valid input and must classify the same way the backend's Pydantic model would — by shape.
 */
function isStringCondition(c: Condition): c is StringCondition {
  return c.kind === "string" || (c.kind === undefined && "values" in c);
}

function isFlagCondition(c: Condition): c is FlagCondition {
  return c.kind === "flag" || (c.kind === undefined && typeof (c as FlagCondition).value === "boolean");
} // prettier-ignore

const NUMERIC_OPS: Record<NumericCondition["op"], (a: number, b: number) => boolean> = {
  lt: (a, b) => a < b,
  lte: (a, b) => a <= b,
  gt: (a, b) => a > b,
  gte: (a, b) => a >= b,
  eq: (a, b) => a === b,
  neq: (a, b) => a !== b,
};

function matches(sku: Sku, c: Condition): boolean {
  if (isStringCondition(c)) {
    const value = String(sku[c.field]);
    const inSet = c.values.includes(value);
    return c.op === "in" ? inSet : !inSet;
  }
  if (isFlagCondition(c)) {
    return Boolean(sku[c.field]) === c.value;
  }
  const numeric = c as NumericCondition;
  return NUMERIC_OPS[numeric.op](Number(sku[numeric.field]), numeric.value);
}

/** A SKU is in the cohort iff every condition in `all` holds AND (if `ids` set) its id ∈ ids. */
export function evalCohort(cohort: CohortDef, skus: readonly Sku[]): Sku[] {
  const conditions = cohort.all ?? [];
  const idSet = cohort.ids ? new Set(cohort.ids) : null;
  return skus.filter((sku) => {
    if (idSet && !idSet.has(sku.id)) {
      return false;
    }
    return conditions.every((c) => matches(sku, c as Condition));
  });
}

function groupKey(sku: Sku, field: GroupByField): string {
  switch (field) {
    case "brand":
      return sku.brand;
    case "band":
      return sku.band;
    case "type":
      return sku.type;
    case "pickReco":
      return sku.pickReco;
    case "lastMovedQ":
      return String(sku.lastMovedQ);
  }
}

/**
 * Sorted ascending, the element at `floor(n/2)` — the UPPER middle for even `n`, never the
 * average of the two middles (pinned in `conventions.median`, `spec_dsl.json`). Keeps a median
 * of counted things integral ("median of 3 accounts", not 2.5) and matches the retired canned
 * agent's `medianReach`.
 */
function median(values: readonly number[]): number {
  if (!values.length) {
    return 0;
  }
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.floor(sorted.length / 2)]!;
}

function aggregate(set: readonly Sku[], metric: Metric): number {
  switch (metric) {
    case "count":
      return set.length;
    case "sum_value":
      return set.reduce((a, s) => a + s.value, 0);
    case "sum_inv":
      return set.reduce((a, s) => a + s.inv, 0);
    case "sum_faces":
      return set.reduce((a, s) => a + s.faces, 0);
    case "median_vel":
      return median(set.map((s) => s.vel));
    case "mean_vel":
      return set.length ? set.reduce((a, s) => a + s.vel, 0) / set.length : 0;
  }
}

export interface GroupRow {
  key: string;
  value: number;
}

/** Live aggregation: cohort → group_by bucket → metric, top_n by value descending. */
export function resolveGroupQuery(query: GroupQuery, skus: readonly Sku[]): GroupRow[] {
  const base = query.cohort ? evalCohort(query.cohort, skus) : skus;
  const groups = new Map<string, Sku[]>();
  for (const sku of base) {
    const key = groupKey(sku, query.group_by);
    const bucket = groups.get(key);
    if (bucket) {
      bucket.push(sku);
    } else {
      groups.set(key, [sku]);
    }
  }
  const metric = query.metric ?? "count";
  const rows = [...groups.entries()].map(([key, set]) => ({ key, value: aggregate(set, metric) }));
  // `conventions.groupSort`: value DESCENDING, ties broken by key ASCENDING (plain lexicographic
  // — not `localeCompare`, whose collation can disagree with Python's code-point `str <` across
  // locales), then truncated to `top_n`.
  rows.sort((a, b) => {
    if (a.value !== b.value) {
      return b.value - a.value;
    }
    return a.key < b.key ? -1 : a.key > b.key ? 1 : 0;
  });
  return rows.slice(0, query.top_n ?? 12);
}

export interface DistributionBin {
  x0: number;
  x1: number;
  count: number;
}

/** A renderer must never trust a spec enough to allocate off it — clamp before anything else. */
const MIN_BINS = 1;
const MAX_BINS = 200;

/**
 * Bin `values` into `bins` buckets over a linear or log1p domain, exactly per
 * `conventions.histogram` in `spec_dsl.json`: `t(v) = v` (linear) or `t(v) = log10(1 + max(0,
 * v))` (log, same transform as the field plot's x axis — `plot-math.ts`'s `toLx` — so a
 * zero-velocity value still lands somewhere rather than at -∞); `lo`/`hi` are the transformed
 * min/max over `values`; `width = (hi - lo) / bins`; bin index = `min(bins - 1, floor((t - lo) /
 * width))`. A degenerate `hi === lo` (all values identical) puts everything in bin 0; an empty
 * cohort returns `bins` all-zero buckets rather than an empty array, so a caller can always
 * assume a `bins`-length result.
 */
export function binDistribution(
  values: readonly number[],
  bins: number,
  scale: "linear" | "log",
): DistributionBin[] {
  const n = Math.min(MAX_BINS, Math.max(MIN_BINS, Math.floor(bins) || MIN_BINS));
  const toT = scale === "log" ? (v: number) => Math.log10(1 + Math.max(0, v)) : (v: number) => v;
  const fromT = scale === "log" ? (t: number) => Math.pow(10, t) - 1 : (t: number) => t;

  if (!values.length) {
    return Array.from({ length: n }, () => ({ x0: 0, x1: 0, count: 0 }));
  }

  const transformed = values.map(toT);
  const lo = Math.min(...transformed);
  const hi = Math.max(...transformed);
  // `hi === lo` (every value transforms identically) would otherwise divide by zero; guarding
  // the width to 1 makes every `t - lo` term 0, which floors to bin 0 for all of them — the
  // degenerate rule the convention calls for, without a separate branch.
  const width = (hi - lo) / n || 1;
  const counts = new Array<number>(n).fill(0);
  for (const t of transformed) {
    const idx = Math.min(n - 1, Math.max(0, Math.floor((t - lo) / width)));
    counts[idx]!++;
  }
  return counts.map((count, i) => ({
    x0: fromT(lo + width * i),
    x1: fromT(lo + width * (i + 1)),
    count,
  }));
}
